/**
 * 마크다운 렌더러 — 저장소의 문서를 콘솔 안에서 그대로 읽게 한다.
 *
 * **왜 라이브러리를 안 쓰나.** 콘솔은 세 가지 방식으로 배포된다(독립 웹 · iframe 임베드 ·
 * 호스트 번들). 의존성 하나가 늘면 세 곳의 번들이 같이 무거워지고, 임베드 쪽은 호스트의
 * CSP 와 부딪힐 여지가 생긴다. 우리 문서가 쓰는 문법은 좁고 이미 다 안다 — 제목·표·코드·
 * 인용·목록·구분선, 그리고 인라인 네 가지. 그 범위만 정확히 처리한다.
 *
 * **안전은 구조로 얻는다.** `dangerouslySetInnerHTML` 을 쓰지 않고 React 엘리먼트를
 * 만들어 돌려주므로, 문서 안의 `<script>` 는 언제나 글자로 남는다. 문서가 저장소에서
 * 오는 지금은 문제가 아니지만, 나중에 사용자가 쓴 글을 같은 렌더러로 그리게 되는 날이
 * 오면 그때는 문제다. 그날을 위해 처음부터 열지 않는다.
 */
import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { copyText } from '../clipboard.js';

export type Block =
  | { kind: 'heading'; level: number; text: string; id: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'code'; lang: string; code: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'rule' };

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'link'; text: string; href: string };

/**
 * 제목 → 앵커 id.
 *
 * 문서 안의 `[§15](#15-구현-현황)` 같은 링크가 실제로 걸리려면 GitHub 과 **같은 규칙**
 * 이어야 한다. 기호를 버리고 공백을 하이픈으로 바꾸는 것이 그 규칙의 핵심이다.
 * 한글은 그대로 둔다 — 우리 문서의 제목은 대부분 한글이라 여기서 버리면 앵커가 다 죽는다.
 */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[`*_~[\]()]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * `| a | b |` 한 줄 → `['a', 'b']`. 양 끝의 빈 칸은 구분자일 뿐이라 버린다.
 *
 * **`\|` 는 칸 구분자가 아니라 글자 `|` 다**(GFM 규칙). 옵션 표의 `--docker auto\|system\|rootless`
 * 처럼 칸 안에 `|` 를 적는 방법이 이것뿐이라, 그대로 자르면 한 칸이 세 칸으로 갈라진다.
 */
export function tableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/(?<!\\)\|$/, '');
  return trimmed.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, '|'));
}

/** `|---|:--:|` 처럼 표의 머리와 몸을 가르는 줄인가. */
function isTableDivider(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line) && line.includes('-');
}

/**
 * 문서를 블록으로 나눈다.
 *
 * 줄 단위로 한 번만 훑는다. 블록 종류가 줄의 **첫 글자**로 거의 결정되기 때문에
 * 그 이상의 구조가 필요 없고, 한 번만 훑으면 500줄짜리 문서도 즉시 그려진다.
 */
export function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  const flushParagraph = (buffer: string[]): void => {
    if (buffer.length === 0) return;
    blocks.push({ kind: 'paragraph', text: buffer.join(' ').trim() });
    buffer.length = 0;
  };

  const paragraph: string[] = [];

  while (index < lines.length) {
    const line = lines[index] ?? '';

    // 코드 블록 — 안쪽은 무엇이든 글자 그대로다. 가장 먼저 걸러야 하는 이유가 그것이다.
    const fence = /^\s*```(.*)$/.exec(line);
    if (fence) {
      flushParagraph(paragraph);
      const lang = (fence[1] ?? '').trim();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !/^\s*```\s*$/.test(lines[index] ?? '')) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      // 닫는 울타리가 없으면 문서 끝까지가 코드다. 여기서 멈추면 나머지가 통째로 사라진다.
      index += 1;
      blocks.push({ kind: 'code', lang, code: body.join('\n') });
      continue;
    }

    if (line.trim() === '') {
      flushParagraph(paragraph);
      index += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph(paragraph);
      const text = (heading[2] ?? '').trim();
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length, text, id: slugify(text) });
      index += 1;
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushParagraph(paragraph);
      blocks.push({ kind: 'rule' });
      index += 1;
      continue;
    }

    // 표 — 머리줄 다음에 구분줄이 와야 표다. 그냥 `|` 로 시작하는 문장과 구별된다.
    if (line.trim().startsWith('|') && isTableDivider(lines[index + 1] ?? '')) {
      flushParagraph(paragraph);
      const header = tableCells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index] ?? '').trim().startsWith('|')) {
        rows.push(tableCells(lines[index] ?? ''));
        index += 1;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushParagraph(paragraph);
      const quoted: string[] = [];
      while (index < lines.length && /^\s*>\s?/.test(lines[index] ?? '')) {
        quoted.push((lines[index] ?? '').replace(/^\s*>\s?/, ''));
        index += 1;
      }
      blocks.push({ kind: 'quote', lines: quoted });
      continue;
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushParagraph(paragraph);
      const ordered = Boolean(numbered);
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const nextBullet = ordered ? /^\s*\d+\.\s+(.*)$/.exec(current) : /^\s*[-*]\s+(.*)$/.exec(current);
        if (nextBullet) {
          items.push((nextBullet[1] ?? '').trim());
          index += 1;
          continue;
        }
        // 들여쓴 이어짐은 앞 항목에 붙인다. 끊으면 한 문장이 두 항목으로 갈라진다.
        if (/^\s{2,}\S/.test(current) && items.length > 0) {
          items[items.length - 1] += ` ${current.trim()}`;
          index += 1;
          continue;
        }
        break;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    paragraph.push(line.trim());
    index += 1;
  }

  flushParagraph(paragraph);
  return blocks;
}

/**
 * 인라인 문법.
 *
 * 순서가 규칙이다. 코드가 가장 세고(그 안은 글자 그대로), 링크, 굵게, 기울임 순이다.
 * `**` 를 `*` 보다 먼저 보지 않으면 굵게가 기울임 두 개로 잘린다.
 */
const INLINE = /(`[^`]+`)|(\[[^\]]*\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)/g;

export function parseInline(text: string): Inline[] {
  const nodes: Inline[] = [];
  let last = 0;

  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) nodes.push({ kind: 'text', text: text.slice(last, at) });
    const token = match[0];

    if (token.startsWith('`')) {
      nodes.push({ kind: 'code', text: token.slice(1, -1) });
    } else if (token.startsWith('[')) {
      const link = /^\[([^\]]*)\]\(([^)\s]+)\)$/.exec(token);
      // 라벨이 비어 있으면 주소를 그대로 보여준다 — 누를 곳이 없어지는 것보다 낫다.
      if (link) nodes.push({ kind: 'link', text: link[1] || (link[2] ?? ''), href: link[2] ?? '' });
    } else if (token.startsWith('**')) {
      nodes.push({ kind: 'strong', text: token.slice(2, -2) });
    } else {
      nodes.push({ kind: 'em', text: token.slice(1, -1) });
    }
    last = at + token.length;
  }

  if (last < text.length) nodes.push({ kind: 'text', text: text.slice(last) });
  return nodes;
}

/**
 * 문서 사이의 링크를 콘솔 주소로 바꾼다.
 *
 * 문서들은 서로를 `[ARCHITECTURE.md](ARCHITECTURE.md)` 로 가리킨다. 그대로 두면
 * 콘솔에서 눌렀을 때 404 가 나거나 파일을 내려받는다 — 읽던 사람이 문서 밖으로 튕긴다.
 */
export function resolveHref(href: string, basePath = ''): string {
  if (/^https?:\/\//.test(href)) return href;

  /*
   * 문서 안 앵커(`#5-배포`)를 라우터 경로에 얹는다.
   *
   * 콘솔은 **해시 라우팅**이라 주소 전체가 이미 `#/docs/plan` 이다. 앵커를 그대로
   * 두면 그것이 라우트를 통째로 덮어써서, 문단으로 가려던 사람이 대시보드로 튕긴다.
   */
  if (href.startsWith('#')) return basePath ? `#${basePath}${href}` : href;

  const doc = /^\.?\/?([A-Z0-9-]+)\.md(#.*)?$/i.exec(href);
  if (doc) return `#/docs/${(doc[1] ?? '').toLowerCase()}${doc[2] ?? ''}`;
  return href;
}

function InlineText({ text, basePath }: { text: string; basePath: string }): ReactNode {
  return (
    <>
      {parseInline(text).map((node, at) => {
        const key = `${node.kind}-${at}`;
        if (node.kind === 'code') {
          return (
            <code
              key={key}
              className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12.5px] text-slate-800"
            >
              {node.text}
            </code>
          );
        }
        if (node.kind === 'strong') {
          return (
            <strong key={key} className="font-semibold text-slate-900">
              {node.text}
            </strong>
          );
        }
        if (node.kind === 'em') {
          return (
            <em key={key} className="italic">
              {node.text}
            </em>
          );
        }
        if (node.kind === 'link') {
          const href = resolveHref(node.href, basePath);
          const external = /^https?:\/\//.test(href);
          return (
            <a
              key={key}
              href={href}
              {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
              className="text-th-3 underline decoration-th-3/30 underline-offset-2 hover:decoration-th-3"
            >
              {node.text}
            </a>
          );
        }
        return <span key={key}>{node.text}</span>;
      })}
    </>
  );
}

const HEADING_CLASS: Readonly<Record<number, string>> = {
  1: 'mt-0 mb-4 text-[24px] font-semibold tracking-tight text-slate-900',
  2: 'mt-10 mb-3 border-t border-slate-200 pt-8 text-[19px] font-semibold tracking-tight text-slate-900',
  3: 'mt-7 mb-2 text-[15.5px] font-semibold text-slate-900',
  4: 'mt-5 mb-2 text-[14px] font-semibold text-slate-700',
  5: 'mt-4 mb-1 text-[13.5px] font-semibold text-slate-700',
  6: 'mt-4 mb-1 text-[13px] font-semibold text-slate-500',
};

/**
 * 코드 블록 — `copyCode` 면 복사 단추를 단다.
 *
 * 설치 매뉴얼처럼 **사람이 그대로 붙여 넣을 명령**을 보여 주는 자리에서 쓴다. 손으로 옮겨 적으면
 * 긴 경로와 따옴표에서 반드시 틀린다. 설계 문서는 읽는 글이라 기본은 끈다.
 */
function CodeBlock({ code, copyCode }: { code: string; copyCode: boolean }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative my-4">
      <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-[12.5px] leading-[1.6] text-slate-800">
        <code>{code}</code>
      </pre>
      {copyCode ? (
        <button
          type="button"
          className="btn btn-tiny absolute right-2 top-2 bg-white"
          onClick={(event) => {
            // 평문 http 의 사내 주소에서도 복사된다(copyText). 못 넣어도 명령은 화면에 보인다.
            void copyText(code, event.currentTarget).then(setCopied);
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2} />}
          {copied ? '복사됨' : '복사'}
        </button>
      ) : null}
    </div>
  );
}

export function Markdown({
  source,
  basePath = '',
  copyCode = false,
}: {
  source: string;
  basePath?: string;
  /** 코드 블록에 복사 단추를 단다. */
  copyCode?: boolean;
}): ReactNode {
  const blocks = parseBlocks(source);

  return (
    <div className="text-[13.5px] leading-[1.75] text-slate-600">
      {blocks.map((block, at) => {
        const key = `${block.kind}-${at}`;

        if (block.kind === 'heading') {
          const Tag = `h${Math.min(block.level, 6)}` as 'h1';
          return (
            <Tag key={key} id={block.id} className={HEADING_CLASS[block.level] ?? HEADING_CLASS[6]}>
              <InlineText text={block.text} basePath={basePath} />
            </Tag>
          );
        }

        if (block.kind === 'paragraph') {
          return (
            <p key={key} className="my-3">
              <InlineText text={block.text} basePath={basePath} />
            </p>
          );
        }

        if (block.kind === 'code') {
          return <CodeBlock key={key} code={block.code} copyCode={copyCode} />;
        }

        if (block.kind === 'list') {
          const Tag = block.ordered ? 'ol' : 'ul';
          return (
            <Tag
              key={key}
              className={`my-3 space-y-1.5 pl-5 ${block.ordered ? 'list-decimal' : 'list-disc'} marker:text-slate-400`}
            >
              {block.items.map((item, itemAt) => (
                <li key={itemAt}>
                  <InlineText text={item} basePath={basePath} />
                </li>
              ))}
            </Tag>
          );
        }

        if (block.kind === 'quote') {
          return (
            <blockquote
              key={key}
              className="my-4 rounded-r-lg border-l-[3px] border-th-5 bg-slate-50 py-3 pl-4 pr-4 text-slate-600"
            >
              {block.lines
                .join('\n')
                .split(/\n{2,}/)
                .map((chunk, chunkAt) => (
                  <p key={chunkAt} className={chunkAt === 0 ? '' : 'mt-2'}>
                    <InlineText text={chunk.replace(/\n/g, ' ')} basePath={basePath} />
                  </p>
                ))}
            </blockquote>
          );
        }

        if (block.kind === 'table') {
          return (
            /* 표는 좁은 화면에서 가로로 넘친다. 문서 전체가 아니라 표만 스크롤하게 가둔다. */
            <div key={key} className="my-4 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[480px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-slate-50">
                    {block.header.map((cell, cellAt) => (
                      <th
                        key={cellAt}
                        className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700"
                      >
                        <InlineText text={cell} basePath={basePath} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowAt) => (
                    <tr key={rowAt} className="border-b border-slate-100 last:border-0">
                      {row.map((cell, cellAt) => (
                        <td key={cellAt} className="px-3 py-2 align-top">
                          <InlineText text={cell} basePath={basePath} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return <hr key={key} className="my-8 border-slate-200" />;
      })}
    </div>
  );
}

/** 문서 안 목차 — h2 만 모은다. h3 까지 넣으면 목차가 본문만큼 길어진다. */
export function outlineOf(source: string): ReadonlyArray<{ id: string; text: string }> {
  return parseBlocks(source)
    .filter((block): block is Extract<Block, { kind: 'heading' }> => block.kind === 'heading' && block.level === 2)
    .map((block) => ({ id: block.id, text: block.text }));
}
